export interface StorybookItem {
  storybookId: string;
  title: string;
  description: string;
  coverImageUrl: string;
  memoryDate: string;
}

export interface StorybookListingData {
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  storybooks: StorybookItem[];
}

export interface StorybookListingRequest {
  pageNumber: number;
  pageSize: number;
  searchText: string;
  sortBy: string | null;
  sortDirection: string | null;
}

export interface StorybookStory {
  storyId: number | string;
  title: string;
  thumbnailUrl: string;
  category: string;
  duration: string;
  mediaType: string;
  isSelected: boolean;
}

export interface GetStorybookStoriesResponse {
  storybookId: number;
  selectedStoriesCount: number;
  totalStories: number;
  viewType: string;
  stories: StorybookStory[];
}

export interface GetStorybookStoriesRequest {
  pageNumber: number;
  pageSize: number;
  searchText: string;
  viewType: string | null;
  storybookId: number;
  categoryId: number | null;
  familySpaceId: string | null;
  creatorIds: string[] | null;
  taggedPersonIds: string[] | null;
}

/** POST body for /Storybooks */
export interface CreateStorybookRequest {
  title: string;
  description: string;
  coverImageUrl: string;
  memoryDate: string;
  /** Order matters — sortOrder (0-based) is the sequence the AI lays out pages in. */
  storyIdList: StorybookStoryIdEntry[];
}

/** Response data shape for POST /Storybooks */
export interface CreateStorybookResponse {
  storybookId: string;
}

/** POST body for /Storybooks/UpdateStorybook */
export interface UpdateStorybookRequest {
  storybookId: string;
  title: string;
  description: string;
  coverImageUrl: string;
  memoryDate: string;
  /** Same shape/convention as CreateStorybookRequest.storyIdList. */
  storyIdList: StorybookStoryIdEntry[];
}

/** One media asset attached to a generated storybook page. */
export interface StorybookPageMedia {
  mediaId: number;
  mediaUrl: string;
  mediaType: string;
  displayOrder: number;
}

/** One generated page inside a storybook. */
export interface StorybookPage {
  pageId: number;
  storyId: string;
  pageOrder: number;
  title: string;
  description: string;
  memoryDate: string;
  mediaType: string;
  qrCodeUrl: string;
  media: StorybookPageMedia[];
}

/** One entry in StorybookDetail.storyIdList — a story included in the book plus its position. */
export interface StorybookStoryIdEntry {
  storyId: string;
  sortOrder: number;
}

/** Response data shape for GET /Storybooks/GetStorybookDetail/{storybookId} */
export interface StorybookDetail {
  storybookId: string;
  title: string;
  description: string;
  coverImageUrl: string;
  memoryDate: string;
  authorName: string;
  totalStories: number;
  totalDuration: number | null;
  estimatedPages: number;
  includesPhotos: boolean;
  includesAIText: boolean;
  includesQRCodes: boolean;
  storyIdList: StorybookStoryIdEntry[];
  pages: StorybookPage[];
}

/** ExportType values for POST /Storybooks/ExportStorybook */
export enum StorybookExportType {
  Pdf = 1,
  Doc = 2,
}

/** POST body for /Storybooks/ExportStorybook */
export interface ExportStorybookRequest {
  storybookId: string;
  exportType: StorybookExportType;
}

/** Response data shape for POST /Storybooks/ExportStorybook */
export interface ExportStorybookResponse {
  /** Base64-encoded file content. */
  fileBytes: string;
  fileName: string;
}

/** Response data shape for POST /Storybooks/GenerateShareableQRCode/{storybookId} */
export interface GenerateShareableQRCodeResponse {
  storybookId: string;
  qrCodeUrl: string;
  shareableLink: string;
}

/** Response data shape for GET /Storybooks/PreviewStorybook/{storybookId} */
export interface StorybookPreview {
  storybookId: string;
  title: string;
  description: string;
  coverImageUrl: string;
  authorName: string;
  memoryDate: string;
  totalPages: number;
  pages: StorybookPage[];
}
